import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../libs/shared/ui/src/styles/mixins';
import {useAppDispatch, useAppSelector} from '../../../helpers/reduxHelpers';
import {isContributingSegmentsOptionsWithSubMetric} from '../../../models/api/analyticsMetricDetailsModel';
import {AnalyticsMetricsKeys, isAnalyticsMetricKey} from '../../../models/api/analyticsMetricsModel';
import {setIsSidePanelOpenAction} from '../../../store/panels/analytics/details/analyticsPanelDetailsActions';
import {
  getAnalyticsDetailsCustomLabel,
  getAnalyticsDetailsSelectedMetricWithOptions,
} from '../../../store/panels/analytics/details/analyticsPanelDetailsSelectors';
import {ContributingSegmentsOptionsEnum, ReportFilterOptionsEnum} from '../../helpers/analyticsReportHelpers';
import {getMetricLabels} from '../../helpers/metricLabels';
import {renderReportSidePanelCloseButton} from './reportSidePanelHelpers';

/*
 * Styles.
 */

const StyledHeaderWrapper = styled.div`
  ${flexCenter}
  justify-content: space-between;
  height: 56px;
  padding: 0 16px;
  color: ${(p) => p.theme.greys.shade70};
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
  overflow-y: hidden;
`;

const StyledHeaderTitle = styled.div`
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.medium};
`;

const StyledHeaderSubtitle = styled.div`
  font-size: ${fontSizes.small};
`;

/*
 * Component.
 */

export const ReportSidePanelHeader: FC = () => {
  const dispatch = useAppDispatch();

  const {selectedMetric, options} = useAppSelector(getAnalyticsDetailsSelectedMetricWithOptions);

  const customLabel = useAppSelector(getAnalyticsDetailsCustomLabel);

  const onRequestClose = () => {
    dispatch(setIsSidePanelOpenAction(false));
  };

  const option = options?.[ReportFilterOptionsEnum.COLLECT_CONTRIBUTING_SEGMENT_IDS];
  const subMetric =
    option && isContributingSegmentsOptionsWithSubMetric(option)
      ? option[ContributingSegmentsOptionsEnum.SUB_METRIC]
      : undefined;

  return (
    <StyledHeaderWrapper>
      {renderHeaderLabel(selectedMetric, subMetric, customLabel)}
      {renderReportSidePanelCloseButton(onRequestClose)}
    </StyledHeaderWrapper>
  );
};

/*
 * Helpers.
 */

function renderHeaderLabel(
  selectedMetric?: AnalyticsMetricsKeys,
  submetric?: string | AnalyticsMetricsKeys,
  customLabel?: string,
) {
  // If we passed a submetriclabel, display it, otherwise fallback to default metric label.

  const {label, columns} = getMetricLabels(selectedMetric);

  let submetricLabel: ReactNode;

  if (columns && submetric) {
    // Table case - we need to get the submetric label from there.
    submetricLabel = columns[submetric]?.label;
  } else if (isAnalyticsMetricKey(submetric)) {
    // Submetric is actually a metric.
    submetricLabel = getMetricLabels(submetric).label;
  }

  return (
    <div>
      {renderHeaderTitle(label, customLabel)}
      {maybeRenderHeaderSubtitle(label, submetricLabel, customLabel)}
    </div>
  );
}

function renderHeaderTitle(metricLabel: string | undefined, customLabel: string | undefined) {
  if (metricLabel) {
    return <StyledHeaderTitle>{metricLabel}</StyledHeaderTitle>;
  } // We have the metric label, we dispay it as the Title.

  return <StyledHeaderTitle>{customLabel}</StyledHeaderTitle>; // Otherwise we display the customLabel as Title.
}

function maybeRenderHeaderSubtitle(
  metricLabel: string | undefined,
  submetricLabel: ReactNode | undefined,
  customLabel: string | undefined,
) {
  if (!metricLabel) {
    return null;
  } // If we don't have the metric label we already displayed custom data as title so no need for a subtitle.

  return <StyledHeaderSubtitle>{submetricLabel || customLabel}</StyledHeaderSubtitle>;
}
